import {FC, useEffect, useMemo, useState} from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Drawer,
  Tooltip,
} from '@mui/material';
import {COVER_FEE_TEXT, TOP} from '@/utils/constants';
import {ReactComponent as QuestionIcon} from '@icons/question.svg';
import {contributions} from './contributions.style';
import Button from '@/shared/components/Button';
import {useAppSelector} from '@/store/hooks';
import {useEventContributions} from '@/utils/hooks/useEventContributions';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import {getUserSelector} from '@/store/user';
import {currencyFormatter} from '@/utils/helpers/currency';
import {calculateFees} from '@/utils/helpers/feeCalculations';
import Checkbox from '@/shared/components/Checkbox';
import {userAPI} from '@/core/api';
import {OrganizationStripeService} from '@/utils/services/organization-stripe.services';
import {getCurrentEventSelector} from '@/store/organization-event';
import {usePaymentStatusPolling} from '@/utils/hooks';

const Contributions: FC = () => {
  const event = useAppSelector(getCurrentEventSelector);
  const user = useAppSelector(getUserSelector);

  // Reset the attendee, balance and pending requests when the account or event changes.
  return <EventContributions key={`${user?.id}:${event?.id}`} />;
};

const EventContributions: FC = () => {
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const user = useAppSelector(getUserSelector);
  const {attendeeId, contributions: attendeeContributions, isLoading, error, refresh} =
    useEventContributions(user?.id, currentEvent?.id);

  const [isCoverFee, setIsCoverFee] = useState(true);

  const {startPollingFromUrl} = usePaymentStatusPolling({
    onCompleted: refresh,
    apiInstance: userAPI,
  });

  const handleMakePayment = async () => {
    if (!currentEvent || !user || !attendeeId || isLoading) return;
    try {
      const {data} = await OrganizationStripeService.paymentUserStripe(attendeeId, {
        isCoverFee,
        eventId: currentEvent?.id,
      });

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const remainingBalance = useMemo(() => {
    const total = Number(attendeeContributions.totalPrice);
    const paid = Number(attendeeContributions.totalPaid);
    if (Number.isNaN(total) || Number.isNaN(paid)) return 0;
    return Math.max(0, total - paid);
  }, [attendeeContributions.totalPrice, attendeeContributions.totalPaid]);

  const fees = useMemo(
    () => calculateFees(remainingBalance, isCoverFee, currentEvent?.organization?.paymentSettings?.transactionFee ?? 0),
    [remainingBalance, isCoverFee, currentEvent?.organization?.paymentSettings?.transactionFee]
  );

  const feePaid = useMemo(() => {
    const paidSumWithoutFee = attendeeContributions.contributions.reduce((acc, item) => {
      return item.isPaid ? acc + item.price : acc;
    }, 0);
    const res = attendeeContributions.totalPaid - paidSumWithoutFee;

    return Number.isNaN(res) ? 0 : res;
  }, [attendeeContributions.contributions, attendeeContributions.totalPaid]);

  const totalContributionsSum = useMemo(() => {
    const itemsSum = attendeeContributions.contributions.reduce((acc, item) => acc + item.price, 0);
    return itemsSum + (isCoverFee ? fees.coverFeeLine : 0);
  }, [attendeeContributions.contributions, fees.coverFeeLine, isCoverFee]);

  const toPay = useMemo(() => Number(fees.amountChargedToPatron), [fees]);

  const disabledButton = useMemo(() => {
    return toPay === 0 || !attendeeId || isLoading;
  }, [toPay, attendeeId, isLoading]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    startPollingFromUrl();
  }, []);

  return (
    <Box sx={contributions.wrapper}>
      <Box
        sx={
          attendeeContributions.contributions && attendeeContributions.contributions.length > 0
            ? {borderBottom: '1px solid var(--neutral-100)'}
            : {}
        }
      >
        <TableContainer>
          <Table sx={contributions.table} aria-label="enhanced table">
            <colgroup>
              <col style={{width: '52%'}} />
              <col style={{width: '16%'}} />
              <col style={{width: '16%'}} />
              <col style={{width: '16%'}} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>
                  <p style={contributions.headText}>Quantity</p>
                </TableCell>
                <TableCell>
                  <p style={contributions.headText}>Paid</p>
                </TableCell>
                <TableCell>
                  <p style={contributions.headText}>Total</p>
                </TableCell>
              </TableRow>
            </TableHead>
            {attendeeContributions.contributions && attendeeContributions.contributions.length > 0 ? (
              <TableBody>
                {attendeeContributions.contributions &&
                  attendeeContributions.contributions.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell sx={contributions.tableCell}>
                        <Box sx={contributions.tableCell}>
                          <p style={contributions.contribution_name}>{row.name}</p>
                          {row.category && <p style={contributions.neutral300}>{row.category}</p>}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={contributions.tableCell}>
                          <p style={contributions.cellText}>{row.amount}</p>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={contributions.tableCell}>
                          <p style={contributions.cellText}>
                            {row.isPaid ? currencyFormatter.format(row.price) : '$0'}
                          </p>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={contributions.tableCell}>
                          <p style={contributions.cellText}>{currencyFormatter.format(row.price)}</p>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                <TableRow>
                  <TableCell sx={contributions.tableCell}>
                    <Box sx={contributions.coverFeeTitle}>
                      <Checkbox value={isCoverFee} onChange={() => setIsCoverFee(!isCoverFee)} />
                      <p style={contributions.contribution_name}>Cover fee</p>

                      <Tooltip title={`${COVER_FEE_TEXT} info`} placement={TOP}>
                        <Box sx={contributions.coverFeeTooltip}>
                          <QuestionIcon />
                        </Box>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell />
                  <TableCell>
                    <Box sx={contributions.tableCell}>
                      <p style={contributions.cellText}>{currencyFormatter.format(feePaid)}</p>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={contributions.tableCell}>
                      <p style={contributions.cellText}>
                        {isCoverFee ? currencyFormatter.format(fees.coverFeeLine) : currencyFormatter.format(0)}
                      </p>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={contributions.tableCell}>
                    <Box sx={contributions.tableCell}>
                      <p style={contributions.totalRowName}>Total contributions</p>
                    </Box>
                  </TableCell>
                  <TableCell />
                  <TableCell>
                    <Box sx={contributions.tableCell}>
                      <p style={contributions.totalText}>{currencyFormatter.format(attendeeContributions.totalPaid)}</p>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={contributions.tableCell}>
                      <p style={contributions.totalText}>{currencyFormatter.format(totalContributionsSum)}</p>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableRow>
                <TableCell colSpan={4} sx={contributions.empty}>
                  <EmptyTableData />
                </TableCell>
              </TableRow>
            )}
          </Table>
        </TableContainer>
      </Box>
      {attendeeContributions.contributions && attendeeContributions.contributions.length > 0 && (
        <Box sx={contributions.toPayWrap}>
          <Typography sx={contributions.toPayText}>Amount Due</Typography>
          <Typography sx={contributions.toPayText}>{currencyFormatter.format(toPay)}</Typography>
        </Box>
      )}
      {attendeeContributions.contributions && attendeeContributions.contributions.length > 0 && (
        <Box sx={contributions.payButtonWrap}>
          <Button
            sx={contributions.payButton}
            title="Complete Payment"
            onClick={handleMakePayment}
            disabled={disabledButton}
          />
        </Box>
      )}

      {attendeeContributions.contributions && attendeeContributions.contributions.length > 0 && (
        <Drawer variant="permanent" anchor="bottom" sx={contributions.drawer}>
          <Box sx={contributions.popup}>
            <Button title="Complete Payment" onClick={handleMakePayment} disabled={disabledButton} />
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

export default Contributions;
